/**
 * streak.js — daily-visit streak tracker stored in localStorage.
 *
 * Returns:
 *   { days: number, isNew: boolean, lastVisitDate: 'YYYY-MM-DD' | null }
 *
 * Rules:
 *   • A visit "counts" once per local calendar day.
 *   • If today === lastVisit  → no change.
 *   • If today === lastVisit + 1 day → streak += 1.
 *   • Otherwise → streak resets to 1 (today started a new streak).
 *
 * Storage key: `blendsafe_streak_v1`
 */
const KEY = "blendsafe_streak_v1";

const todayISO = () => new Date().toISOString().slice(0, 10);

const dayDiff = (a, b) => {
  const da = new Date(`${a}T00:00:00Z`).getTime();
  const db = new Date(`${b}T00:00:00Z`).getTime();
  return Math.round((db - da) / 86_400_000);
};

const safeRead = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj && typeof obj.days === "number" && typeof obj.last === "string") {
      return obj;
    }
    return null;
  } catch {
    return null;
  }
};

const safeWrite = (obj) => {
  try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch { /* noop */ }
};

export function recordVisit() {
  const today = todayISO();
  const prev = safeRead();
  if (!prev) {
    safeWrite({ days: 1, last: today });
    return { days: 1, isNew: true, lastVisitDate: today };
  }
  if (prev.last === today) {
    return { days: prev.days, isNew: false, lastVisitDate: prev.last };
  }
  const diff = dayDiff(prev.last, today);
  const next = diff === 1 ? prev.days + 1 : 1;
  safeWrite({ days: next, last: today });
  return { days: next, isNew: next === 1 || diff === 1, lastVisitDate: today };
}

export function getStreak() {
  const prev = safeRead();
  if (!prev) return { days: 0, isNew: false, lastVisitDate: null };
  return { days: prev.days, isNew: false, lastVisitDate: prev.last };
}
